import { Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import {
  PrinterIcon,
  ShareIcon,
  DocumentArrowDownIcon,
  ChevronDownIcon,
  LinkIcon,
  EnvelopeIcon,
  DocumentTextIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';

interface ReportActionsProps {
  reportTitle?: string;
  className?: string;
  /** Canonical share URL (deep link). Defaults to window.location.href. */
  shareUrl?: string;
  onPrint?: () => void;
  onExportPDF?: () => void | Promise<void>;
  onExportCSV?: () => void;
  onEmailShare?: () => void | Promise<void>;
  showDateRange?: string;
  reportData?: unknown;
}

export default function ReportActions({
  reportTitle = 'BirdMan Reports',
  className = '',
  shareUrl,
  onPrint,
  onExportPDF,
  onExportCSV,
  onEmailShare,
  showDateRange,
  reportData,
}: ReportActionsProps) {
  const resolvedShareUrl = shareUrl || (typeof window !== 'undefined' ? window.location.href : '');

  const handlePrint = () => {
    if (onPrint) {
      onPrint();
    } else {
      window.print();
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: reportTitle,
      text: `${reportTitle} for ${format(new Date(), 'PPP')}${showDateRange ? ` (${showDateRange})` : ''}`,
      url: resolvedShareUrl,
    };

    if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData);
        toast.success('Report shared successfully');
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          console.error('Error sharing:', error);
          handleFallbackShare();
        }
      }
    } else {
      handleFallbackShare();
    }
  };

  const handleFallbackShare = () => {
    navigator.clipboard.writeText(resolvedShareUrl);
    toast.success('Report URL copied to clipboard');
  };

  const handleEmailShare = async () => {
    if (onEmailShare) {
      await onEmailShare();
      return;
    }
    const subject = encodeURIComponent(reportTitle);
    const body = encodeURIComponent(
      `I wanted to share this report with you:\n\n${reportTitle}\nGenerated on ${format(new Date(), 'PPP')}\n\nView the full report at: ${resolvedShareUrl}`,
    );
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
  };

  const handleExportPDF = async () => {
    if (onExportPDF) {
      await onExportPDF();
      return;
    }
    try {
      const reportContent = `${reportTitle}\nGenerated on: ${new Date().toLocaleDateString()}\n\n${JSON.stringify(reportData, null, 2)}`;
      const blob = new Blob([reportContent], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${reportTitle.replace(/\s+/g, '_').toLowerCase()}_${new Date().toISOString().split('T')[0]}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('Report exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export report');
    }
  };

  const handleExportCSV = () => {
    if (onExportCSV) {
      onExportCSV();
      return;
    }
    try {
      let csvContent = `${reportTitle}\nGenerated on: ${new Date().toLocaleDateString()}\n\n`;
      if (Array.isArray(reportData)) {
        if (reportData.length > 0) {
          const headers = Object.keys(reportData[0] as object);
          csvContent += headers.join(',') + '\n';
          reportData.forEach((row) => {
            const values = headers.map((header) => {
              const value = (row as Record<string, unknown>)[header];
              return typeof value === 'string' && value.includes(',') ? `"${value}"` : value;
            });
            csvContent += values.join(',') + '\n';
          });
        }
      } else if (typeof reportData === 'object' && reportData) {
        Object.entries(reportData as Record<string, unknown>).forEach(([key, value]) => {
          csvContent += `${key},${typeof value === 'object' ? JSON.stringify(value) : value}\n`;
        });
      }
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${reportTitle.replace(/\s+/g, '_').toLowerCase()}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('CSV exported successfully');
    } catch (error) {
      console.error('CSV export error:', error);
      toast.error('Failed to export CSV');
    }
  };

  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      <button
        onClick={handlePrint}
        className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50/80 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2"
        title="Print report"
      >
        <PrinterIcon className="h-4 w-4 mr-2" />
        Print
      </button>

      <Menu as="div" className="relative inline-block text-left">
        <div>
          <Menu.Button className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50/80 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2">
            <ShareIcon className="h-4 w-4 mr-2" />
            Share
            <ChevronDownIcon className="h-4 w-4 ml-1" />
          </Menu.Button>
        </div>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
            <div className="py-1">
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={handleShare}
                    className={`${
                      active ? 'bg-gray-100 text-slate-900' : 'text-slate-700'
                    } group flex w-full items-center px-4 py-2 text-sm`}
                  >
                    <LinkIcon className="mr-3 h-4 w-4" />
                    Copy Link
                  </button>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={() => void handleEmailShare()}
                    className={`${
                      active ? 'bg-gray-100 text-slate-900' : 'text-slate-700'
                    } group flex w-full items-center px-4 py-2 text-sm`}
                  >
                    <EnvelopeIcon className="mr-3 h-4 w-4" />
                    Share via Email
                  </button>
                )}
              </Menu.Item>
            </div>
          </Menu.Items>
        </Transition>
      </Menu>

      <Menu as="div" className="relative inline-block text-left">
        <div>
          <Menu.Button className="inline-flex items-center justify-center rounded-md border border-transparent bg-cyan-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2">
            <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
            Export
            <ChevronDownIcon className="h-4 w-4 ml-1" />
          </Menu.Button>
        </div>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
            <div className="py-1">
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={() => void handleExportPDF()}
                    className={`${
                      active ? 'bg-gray-100 text-slate-900' : 'text-slate-700'
                    } group flex w-full items-center px-4 py-2 text-sm`}
                  >
                    <DocumentTextIcon className="mr-3 h-4 w-4" />
                    Export as PDF
                  </button>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={handleExportCSV}
                    className={`${
                      active ? 'bg-gray-100 text-slate-900' : 'text-slate-700'
                    } group flex w-full items-center px-4 py-2 text-sm`}
                  >
                    <TableCellsIcon className="mr-3 h-4 w-4" />
                    Export as CSV
                  </button>
                )}
              </Menu.Item>
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
    </div>
  );
}
